/**
 * RevenueCat metrics for dashboard applications that have it enabled.
 *
 * Application-agnostic: every helper takes the selected dashboard `app_id` and consults
 * REVENUECAT_ENABLED_APPS below. Applications absent from that map keep using IAPHUB and
 * never issue a RevenueCat request at all, so their render path is unchanged.
 *
 * Rules enforced here:
 *  - RevenueCat values REPLACE the corresponding IAPHUB values for an enabled app; they are
 *    never added to them, so revenue cannot be double-counted.
 *  - Metrics not listed for an app stay on IAPHUB.
 *  - Installs / uninstalls are never sourced from RevenueCat (GA4 only).
 *  - The RevenueCat secret key lives only in the backend environment; the browser sees
 *    aggregated numbers.
 *
 * To enable another application: add an entry to REVENUECAT_ENABLED_APPS whose key matches
 * the backend's revenuecat_apps.REVENUECAT_APPS entry. Nothing else changes.
 */

export const METRIC_REVENUE = 'revenue';
export const METRIC_REVENUE_TRENDS = 'revenue_trends';
export const METRIC_REVENUE_BY_PRODUCT_DURATION = 'revenue_by_product_duration';
export const METRIC_ACTIVE_SUBSCRIPTIONS = 'active_subscriptions';

export const ALL_METRICS = [
  METRIC_REVENUE,
  METRIC_REVENUE_TRENDS,
  METRIC_REVENUE_BY_PRODUCT_DURATION,
  METRIC_ACTIVE_SUBSCRIPTIONS,
];

/**
 * Dashboard app id -> RevenueCat-enabled metrics.
 * Must mirror the backend registry; the backend refuses anything not configured there.
 */
export const REVENUECAT_ENABLED_APPS = {
  // VetRA — its own RevenueCat account/project.
  '60d19b028ac2e540ca51bbdc': { name: 'VetRA', metrics: ALL_METRICS },
  // MSK US Knee — a separate RevenueCat account/project from VetRA's.
  '610280ea01c6203e11f777ea': { name: 'MSK US Knee', metrics: ALL_METRICS },
};

/**
 * Bump CACHE_SCHEMA_VERSION whenever the cached (normalized) payload gains or renames a
 * field. Cached entries are read back by a possibly newer build, and an old-shape payload
 * that still looks "available" would otherwise satisfy the new code and short-circuit the
 * refetch.
 */
const CACHE_SCHEMA_VERSION = 3;
const CACHE_STORAGE_KEY = `subsDash_revenueCatCache_v${CACHE_SCHEMA_VERSION}`;
const _LEGACY_CACHE_STORAGE_KEYS = [
  'subsDash_vetraRevenueCatCache',
  'subsDash_vetraRevenueCatCache_v2',
];

const _cache = new Map();

const _readSessionCache = () => {
  try {
    const raw = sessionStorage.getItem(CACHE_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

/** Reject anything that does not match the current normalized shape. */
const _isCurrentShape = (value) =>
  Boolean(value) && typeof value === 'object' &&
  value.activeSubscriptions != null && typeof value.activeSubscriptions === 'object';

// Drop caches written by earlier builds so they cannot be read back under a new shape.
_LEGACY_CACHE_STORAGE_KEYS.forEach((key) => {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
});

Object.entries(_readSessionCache()).forEach(([key, value]) => {
  if (_isCurrentShape(value)) _cache.set(key, value);
});

const _persistCache = () => {
  try {
    sessionStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(Object.fromEntries(_cache.entries())));
  } catch {
    // quota or private mode — the in-memory cache still works
  }
};

/** Drop the cached responses (app switches keep them; used by tests and manual refresh). */
export const clearRevenueCatCache = () => {
  _cache.clear();
  try {
    sessionStorage.removeItem(CACHE_STORAGE_KEY);
    _LEGACY_CACHE_STORAGE_KEYS.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // ignore — the in-memory cache is already cleared
  }
};

/** True only for applications configured for RevenueCat. Gates every RevenueCat path. */
export const isRevenueCatEnabled = (appId) =>
  Boolean(appId && Object.prototype.hasOwnProperty.call(REVENUECAT_ENABLED_APPS, appId));

/** True when a specific metric is RevenueCat-backed for this application. */
export const isRevenueCatMetricEnabled = (appId, metric) => {
  const entry = REVENUECAT_ENABLED_APPS[appId];
  return Boolean(entry && entry.metrics.includes(metric));
};

const _toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Active Subscriptions block — point-in-time, with its own availability flag so a failure
 * here does not disturb the revenue metrics (and vice versa).
 *
 * `total` stays null when unavailable. It is never coerced to 0, because 0 would render as
 * a plausible-looking count, and it is never replaced by the IAPHUB value.
 */
export const normalizeActiveSubscriptions = (raw) => {
  if (!raw || typeof raw !== 'object') {
    return { available: false, error: null, total: null, byStore: {}, storeLabels: [], asOf: null, incomplete: false };
  }
  const byStore = raw.byStore && typeof raw.byStore === 'object' ? raw.byStore : {};
  const hasTotal = raw.total !== null && raw.total !== undefined && Number.isFinite(Number(raw.total));
  return {
    available: Boolean(raw.available) && hasTotal,
    error: raw.error || null,
    total: hasTotal ? Number(raw.total) : null,
    byStore,
    storeLabels: Object.keys(byStore),
    asOf: raw.asOf || null,
    incomplete: Boolean(raw.incomplete),
  };
};

/**
 * Normalize the backend payload so the dashboard can read it without extra guards.
 * Returns null when the argument is not a usable object.
 */
export const normalizeRevenueCatMetrics = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  const revenueByStore =
    raw.revenueByStore && typeof raw.revenueByStore === 'object' ? raw.revenueByStore : {};
  return {
    source: raw.source || 'revenuecat',
    appId: raw.appId || null,
    appName: raw.appName || null,
    configured: Boolean(raw.configured),
    available: Boolean(raw.available),
    error: raw.error || null,
    warnings: Array.isArray(raw.warnings) ? raw.warnings : [],
    currency: raw.currency || 'USD',
    revenueType: raw.revenueType || null,
    today: raw.today || null,
    todayIncomplete: Boolean(raw.todayIncomplete),
    timezone: raw.timezone || null,
    includedAppIds: Array.isArray(raw.includedAppIds) ? raw.includedAppIds : [],
    excludedAppIds: Array.isArray(raw.excludedAppIds) ? raw.excludedAppIds : [],
    enabledMetrics: Array.isArray(raw.enabledMetrics) ? raw.enabledMetrics : [],
    todayRevenue: _toNumber(raw.todayRevenue),
    filteredPeriodRevenue: _toNumber(raw.filteredPeriodRevenue),
    dailyRevenue: Array.isArray(raw.dailyRevenue)
      ? raw.dailyRevenue.map((row) => ({
          date: row?.date,
          revenue: _toNumber(row?.revenue),
        }))
      : [],
    productBreakdown: Array.isArray(raw.productBreakdown)
      ? raw.productBreakdown.map((row) => ({
          name: row?.name ?? 'Unknown',
          value: _toNumber(row?.value),
          // null on purpose: RevenueCat's revenue chart carries money, not counts.
          count: row?.count == null ? null : _toNumber(row.count),
          revenuePercent: _toNumber(row?.revenuePercent),
          countPercent: _toNumber(row?.countPercent),
        }))
      : [],
    revenueByStore,
    // Store names come from the response's per-store split (e.g. "App Store", "Play Store").
    storeLabels: Object.keys(revenueByStore),
    unattributedRevenue: _toNumber(raw.unattributedRevenue),
    activeSubscriptions: normalizeActiveSubscriptions(raw.activeSubscriptions),
  };
};

/**
 * Fetch RevenueCat metrics for a dashboard application.
 *
 * Resolves to null for an application without RevenueCat enabled or when the API base URL
 * is missing, and to a normalized payload with available=false when RevenueCat is
 * unconfigured or failing — the caller then keeps its IAPHUB values.
 */
export const fetchRevenueCatMetrics = async (appId, startStr, endStr) => {
  if (!isRevenueCatEnabled(appId)) return null;

  const baseUrl = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
  if (!baseUrl) return null;

  const cacheKey = `${appId}|${startStr}|${endStr}`;
  const cached = _cache.get(cacheKey);
  // Only serve a cache hit that matches the current shape; anything else is refetched
  // rather than short-circuiting with a payload the caller cannot read.
  if (_isCurrentShape(cached)) return cached;
  if (cached) _cache.delete(cacheKey);

  const params = new URLSearchParams({
    app_id: appId,
    start_date: startStr,
    end_date: endStr,
  });

  try {
    const res = await fetch(`${baseUrl}/revenuecat/revenue-metrics?${params}`);
    if (!res.ok) {
      console.warn(`RevenueCat metrics unavailable for ${appId}: HTTP ${res.status}`);
      return normalizeRevenueCatMetrics({
        configured: false,
        available: false,
        error: `Backend returned HTTP ${res.status}`,
      });
    }
    const data = normalizeRevenueCatMetrics(await res.json());
    // Only cache usable responses so a transient outage is retried.
    if (data && data.available) {
      _cache.set(cacheKey, data);
      _persistCache();
    }
    return data;
  } catch (error) {
    console.error(`Error fetching RevenueCat metrics for ${appId}:`, error);
    return normalizeRevenueCatMetrics({
      configured: false,
      available: false,
      error: String(error?.message || error),
    });
  }
};

/** True when RevenueCat revenue figures should replace the IAPHUB ones. */
export const isRevenueCatRevenueActive = (appId, metrics) =>
  Boolean(isRevenueCatMetricEnabled(appId, METRIC_REVENUE) && metrics && metrics.available);

/** True when the Active Subscriptions card should show the RevenueCat count. */
export const isRevenueCatActiveSubscriptionsActive = (appId, metrics) =>
  Boolean(
    isRevenueCatMetricEnabled(appId, METRIC_ACTIVE_SUBSCRIPTIONS) &&
      metrics?.activeSubscriptions?.available
  );

export default fetchRevenueCatMetrics;
